import React, { lazy, Suspense } from "react";

import { Outlet } from "react-router-dom";
import { InitializeScripts } from "@apicontrols/InitializeScripts.js";
import CountryPermission from "@controls/CountryPermission.js";
import { Dialog } from "@controls/Dialogs/index.js";
import DisplayPoints from "@controls/DisplayPoints.js";
import { FileDownloader } from "@controls/FileDownloader.js";
import FileProcessor from "@controls/FileProcessor.js";
import { Filter } from "@controls/Filter/index.js";
import FreshTranslations from "@controls/FreshTranslations.js";
import Headers from "@controls/Headers.js";
import { LoadClientSideOnly } from "@controls/LazyLoader.js";
import MakeTranslation from "@controls/MakeTranslation.js";
import ProcessChildren from "@controls/ProcessChildren.js";
import ResponsiveAds from "@controls/ResponsiveAds/index.js";
import { ScrollEvents, ScrollSuspense } from "@controls/ScrollEvents.js";
import {
  CFW_CURRENT_TRANSLATION,
  CFW_CURRENT_URL,
  CFW_IS_MOBILE,
  Response_TranslationData,
} from "@genericComponents/CFW.js";
import { GenerateNameFromUrl } from "@genericComponents/Common.js";
import { HandleSEO } from "@genericComponents/HandleSEO.js";
import Loading from "@genericComponents/Loading.js";
import { GetConfig } from "@genericComponents/SFW.js";
import {
  GetState,
  globalVariables,
  SetState,
} from "@genericComponents/StoreToolkit.js";
import { Header } from "@headers/index.js";
import { API_URL } from "@data/GlobalData.js";
import PageSelector from "@views/PageSelector/PageSelector.js";
const useProductLanguageCode =
  await import("@customHooks/useProductLanguageCode.js").then(
    (mod) => mod.useProductLanguageCode,
  );
const Footer = lazy(() => import("@genericComponents/Footer.js"));
const HTML = ({ children }) => {
  const { languageCode } = useProductLanguageCode(true);
  globalVariables[CFW_CURRENT_TRANSLATION] =
    `${Response_TranslationData}-${languageCode}`;
  return (
    <html lang={languageCode} prefix="og: https://ogp.me/ns#">
      {children}
    </html>
  );
};
const App = ({ currentUrl, css, roles, initData, isDefaultMobile }) => {
  for (const item of Object.keys(initData)) {
    if (
      !Array.isArray(initData[item]) ||
      (Array.isArray(initData[item]) && initData[item].length)
    ) {
      SetState(item, initData[item]);
    }
  }
  SetState(CFW_CURRENT_URL, currentUrl);
  SetState(CFW_IS_MOBILE, isDefaultMobile);
  return (
    <HTML>
      <head prefix="og: https://ogp.me/ns# fb: https://ogp.me/ns/fb# website: https://ogp.me/ns/website#">
        <Headers />
        <meta
          name="google-site-verification"
          content="M4gIOEJTwVwVTq9KT-loFMhqakoKVZVZeegSgyrqvfQ"
        />
        <meta charSet="utf-8" />
        <meta
          name="google-adsense-account"
          content={`ca-pub-${GetConfig("CompanyAdsenseId")}`}
        ></meta>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#000000" />
        <meta property="og:type" content="website" />
        <meta name="revisit-after" content="15 days" />
        {css.map((cssPath) => (
          <link
            id={GenerateNameFromUrl(cssPath)}
            key={cssPath}
            rel="stylesheet"
            href={cssPath}
          ></link>
        ))}
        <meta
          data-n-head="ssr"
          data-hid="og:image:type"
          property="og:image:type"
          content="image/png"
        />
        <meta
          property="fb:app_id"
          content={`"${GetConfig("FacebookAppId")}"`}
        />
        <meta
          name="facebook-domain-verification"
          content="ofe1rrdk4p03ze9q7qqdxo694qyqiv"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="icon" href={`${API_URL}file/SiteImages/favicon.ico`} />
        <link rel="dns-prefetch" href="https://urtechcity.com/manifest.json" />
        <link
          rel="manifest"
          href="/manifest.json"
          crossOrigin="use-credentials"
        />
      </head>
      <body cz-shortcut-listen="true">
        <div id="fb-root" />
        <Header />
        <FreshTranslations />
        <CountryPermission />
        <InitializeScripts isMobile={isDefaultMobile} />
        <LoadClientSideOnly>
          <DisplayPoints />
        </LoadClientSideOnly>
        <ScrollEvents />
        <main role="main" className="mainContent">
          <article>
            <Suspense fallback={<Loading />}>
              <Outlet />
            </Suspense>
            <PageSelector roles={roles} />
          </article>
        </main>
        <MakeTranslation />
        {!GetState(CFW_CURRENT_URL)
          .toLowerCase()
          .includes("/dailytimerecord/") && (
          <ScrollSuspense scrollcounter={2}>
            <ResponsiveAds
              slot={GetConfig("CompanyAdSlotMultiplex")}
              format="autorelaxed"
            />
          </ScrollSuspense>
        )}
        <Suspense fallback={<Loading />}>
          <Footer />
        </Suspense>
        <Dialog />
        <Filter />
        <FileDownloader />
        <FileProcessor />
        <ProcessChildren />
        <HandleSEO />
      </body>
    </HTML>
  );
};
export default App;
